import { ResetPasswordProps } from "@/types/auth"
import { CollectionEmbeddedProps } from "@/types/collection"
import { EmbeddedProps } from "@/types/embedded"
import { PlaceRef } from "@/types/entity"
import { FlightBookingMode } from "@/types/flights/flight-booking"
import { FlightBookingApi } from "@/types/flights/flight-booking-api"
import { TripRef } from "@/types/trip"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { FlightBookingFormOptions } from "@/lib/flight-form-utils"
import { RootState } from "@/lib/storage/redux-store"
import { GuideProps } from "@/components/guide"
import { JoinTripProps } from "@/components/join-trip"
import { PlaceDetailPageProps } from "@/components/place-detail/place-detail-page"
import { ProfileProps } from "@/components/profile"
import { SettingsProps } from "@/components/settings"
import { ShareProps } from "@/components/share"
import { VerifyEmailProps } from "@/components/verify-email"
import { selectEmbedded } from "./app-slice"

export type ChatView = {
  type: "chat"
}

export type SavedView = {
  type: "saved"
}

export type InspirationView = {
  type: "inspiration"
}

export type ExploreView = {
  type: "explore"
}

export type GuideView = {
  type: "guide"
  props: GuideProps
}

export type TripsView = {
  type: "trips"
}

export type TripView = {
  type: "trip"
  props: { ref: TripRef }
}

export type CollectionView = {
  type: "collection"
  props: CollectionEmbeddedProps
}

export type ProfileView = {
  type: "profile"
  props: ProfileProps
}

export type PublicProfileView = {
  type: "public-profile"
  props: { handle: string }
}

export type ShareView = {
  type: "share"
  props: ShareProps
}

export type PlaceView = {
  type: "place"
  props: PlaceDetailPageProps
}

export type HotelRoomsView = {
  type: "hotel-rooms"
  props: {
    placeRef: PlaceRef
    start: string | null
    end: string | null
    adults: number | null
    children: number | null
    childAges: number[] | null
    tripRef: TripRef | null
    eventId: number | null
    selectedRoomId: number | null
    hotelQuoteRef: string | null
    currency: string | null
    bookingError: string | null
    recoveryToken: string | null
  }
}

export type FlightBookingView = {
  type: "flight-booking"
  props: {
    inFlightBooking?: FlightBookingApi
    inFormOptions?: FlightBookingFormOptions
    inError?: string
    inExpired?: boolean
    inBookingMode: FlightBookingMode
    vendorCheckoutUrl: string | null
  }
}

export type SettingsView = {
  type: "settings"
  props: SettingsProps
}

export type JoinTripView = {
  type: "join-trip"
  props: JoinTripProps
}

export type VerifyEmailView = {
  type: "verify-email"
  props: VerifyEmailProps
}

export type CheckSettingsView = {
  type: "check-settings"
}

export type ResetPasswordView = {
  type: "reset-password"
  props: ResetPasswordProps
}

export type ErrorView = {
  type: "error"
}

// empty view used during init/loading
export type EmptyView = {
  type: "empty"
}

export type EmbeddedView =
  | ChatView
  | SavedView
  | InspirationView
  | ExploreView
  | GuideView
  | TripsView
  | TripView
  | CollectionView
  | ProfileView
  | PublicProfileView
  | ShareView
  | PlaceView
  | HotelRoomsView
  | FlightBookingView
  | SettingsView
  | JoinTripView
  | VerifyEmailView
  | ResetPasswordView
  | CheckSettingsView
  | ErrorView
  | EmptyView

export interface EmbeddedSliceState {
  view: EmbeddedView
  isInitApp: boolean
  isInitChat: boolean
  localeChanged: boolean
  renderCreateAnonTurnstile: boolean
  props: EmbeddedProps | undefined
}

export const embeddedSliceInitialState: EmbeddedSliceState = {
  view: {
    type: "empty",
  },
  isInitApp: false,
  isInitChat: false,
  localeChanged: false,
  renderCreateAnonTurnstile: false,
  props: undefined,
}

const embeddedSlice = createSlice({
  name: "embeddedSlice",
  initialState: embeddedSliceInitialState,
  reducers: {
    setView: (state, action: PayloadAction<EmbeddedView>) => {
      state.view = action.payload
    },
    setEmbeddedProps: (state, action: PayloadAction<EmbeddedProps>) => {
      state.props = action.payload
    },
    setEmbeddedIsInitApp: (state, action: PayloadAction<boolean>) => {
      state.isInitApp = action.payload
    },
    setEmbeddedIsInitChat: (state, action: PayloadAction<boolean>) => {
      state.isInitChat = action.payload
    },
    setLocaleChanged: (state, action: PayloadAction<boolean>) => {
      state.localeChanged = action.payload
    },
    setRenderCreateAnonTurnstile: (state, action: PayloadAction<boolean>) => {
      state.renderCreateAnonTurnstile = action.payload
    },
  },
})

const selectSlice = (state: RootState) => state.embeddedSlice

export const selectEmbeddedView = createSelector(
  [selectSlice],
  (state) => state.view
)

export const selectIsEmbeddedChatView = createSelector(
  [selectEmbedded, selectEmbeddedView],
  (embedded, view) => embedded && view.type === "chat"
)

export const selectIsEmbeddedExploreView = createSelector(
  [selectEmbedded, selectEmbeddedView],
  (embedded, view) => embedded && view.type === "explore"
)

export const selectEmbeddedProps = createSelector(
  [selectSlice],
  (state) => state.props
)

export const selectEmbeddedIsInitChat = createSelector(
  [selectSlice],
  (state) => state.isInitChat
)

export const selectLocaleChanged = createSelector(
  [selectSlice],
  (state) => state.localeChanged
)

export const selectEmbeddedIsInitApp = createSelector(
  [selectSlice],
  (state) => state.isInitApp
)

export const selectRenderCreateAnonTurnstile = createSelector(
  [selectSlice],
  (state) => state.renderCreateAnonTurnstile
)

export const {
  setView,
  setEmbeddedProps,
  setEmbeddedIsInitChat,
  setEmbeddedIsInitApp,
  setLocaleChanged,
  setRenderCreateAnonTurnstile,
} = embeddedSlice.actions

export default embeddedSlice
